/**
 * Enum representing the severity levels of telemetry items.
 *
 * @remarks
 * This enum is used to categorize telemetry items based on their severity level.
 * It helps in filtering and prioritizing telemetry data for monitoring and analysis.
 *
 * @enum
 * @property {TelemetryLevel.Debug} Debug - Debugging information useful during development.
 * @property {TelemetryLevel.Information} Information - General information about the system's operation.
 * @property {TelemetryLevel.Warning} Warning - Indicates a potential issue that is not critical.
 * @property {TelemetryLevel.Error} Error - Represents an error that has occurred, but the system can continue running.
 * @property {TelemetryLevel.Critical} Critical - A severe error that may cause the system to stop functioning.
 */
export var TelemetryLevel;
(function (TelemetryLevel) {
    TelemetryLevel[TelemetryLevel["Debug"] = 0] = "Debug";
    TelemetryLevel[TelemetryLevel["Information"] = 1] = "Information";
    TelemetryLevel[TelemetryLevel["Warning"] = 2] = "Warning";
    TelemetryLevel[TelemetryLevel["Error"] = 3] = "Error";
    TelemetryLevel[TelemetryLevel["Critical"] = 4] = "Critical";
})(TelemetryLevel || (TelemetryLevel = {}));
/**
 * Enum representing the different types of telemetry data that can be tracked.
 *
 * @remarks
 * This enum is used to categorize telemetry entries for logging, monitoring, and analytics purposes.
 *
 * @enum
 * @property {TelemetryType.Event} Event - Represents a general event telemetry.
 * @property {TelemetryType.Exception} Exception - Represents an exception or error telemetry.
 * @property {TelemetryType.Metric} Metric - Represents a metric or measurement telemetry.
 * @property {TelemetryType.Custom} Custom - Represents a custom-defined telemetry type.
 */
export var TelemetryType;
(function (TelemetryType) {
    TelemetryType["Event"] = "event";
    TelemetryType["Exception"] = "exception";
    TelemetryType["Metric"] = "metric";
    TelemetryType["Custom"] = "custom";
})(TelemetryType || (TelemetryType = {}));
/**
 * Defines the available scopes for telemetry data collection.
 *
 * - `Framework`: Telemetry related to the underlying framework.
 * - `Application`: Telemetry specific to the application layer.
 */
export var TelemetryScope;
(function (TelemetryScope) {
    TelemetryScope["Framework"] = "framework";
    TelemetryScope["Application"] = "application";
})(TelemetryScope || (TelemetryScope = {}));
/**
 * Standard telemetry item names for common error types.
 */
export const TelemetryItemNames = {
    /** Error name used when metadata extraction fails */
    MetadataError: 'TelemetryMetadataError',
};
//# sourceMappingURL=static.js.map